// src/pages/MobileComms.tsx  — Phase 5 (NotificationsHubTab only)
// ─────────────────────────────────────────────────────────────────
// MOBILE & CREW — Phase 5 Update
//
// Phase 5 changes over Phase 4:
//   • NotificationsHubTab — fully rebuilt:
//       PM view (all crew) + simulated crew view (own only)
//       Type / job / crew / date filters + keyword search
//       Date grouping (Today / Yesterday / This week / Earlier)
//       Rich expandable notification cards per type
//       Per-crew delivery status with acknowledged names
//       Resend to unacknowledged
//       Escalate to WHSO alert (creates DRAFT in WHS tab)
//       CSV export for WHS audit trail
//
// Merge instructions:
//   Replace ONLY the NotificationsHubTab function in the Phase 4
//   MobileComms.tsx. All other components are unchanged.
//   In the main MobileComms() component, also update the
//   tab config line to add the "5B connections" badge logic
//   (see bottom of this file).
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo } from 'react'

// ── Services ──────────────────────────────────────────────────────
import {
  getCrewNotifications,
  acknowledgeNotification,
  getUnreadCount,
  resendNotification,
  escalateToWHSOAlert,
  getDeliveryStatusSummary,
  getNotificationsGroupedByDate,
  searchNotifications,
  exportNotificationsToCSV,
  type GroupedNotifications,
  type DeliveryStatusSummary,
} from '../services/notificationBusService'

// ── Types ─────────────────────────────────────────────────────────
import type { CrewNotification, NotificationType, AccommodationPayload, PermitToWorkPayload, TimesheetApprovedPayload, MaterialReadyPayload, ITPReleasePayload } from '../types/mobileCrewTypes'
import { NOTIFICATION_TYPE_META, MATERIAL_STATUS_META } from '../types/mobileCrewTypes'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'

// ── Icons ─────────────────────────────────────────────────────────
import {
  Bell, Check, X, RefreshCw, Search, Filter, Download,
  ChevronDown, ChevronUp, Phone, MapPin, Clock,
  CheckCircle, Circle, AlertTriangle, Send,
  ArrowUpRight, Users, Zap, Eye, FileText,
  Shield, Package, Calendar, Info, ExternalLink,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────
function timeAgo(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
function fmtAEST(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    timeZone:'Australia/Brisbane', day:'numeric', month:'short',
    hour:'2-digit', minute:'2-digit',
  })
}

const PRIORITY_RING: Record<string, string> = {
  CRITICAL: 'border-l-4 border-l-red-500',
  HIGH:     'border-l-4 border-l-amber-400',
  MEDIUM:   'border-l-4 border-l-blue-400',
  LOW:      'border-l-4 border-l-gray-200',
}
const PRIORITY_BADGE: Record<string, string> = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH:     'bg-red-100 text-red-700',
  MEDIUM:   'bg-amber-100 text-amber-800',
  LOW:      'bg-gray-100 text-gray-500',
}

// ─────────────────────────────────────────────────────────────────
// RICH PAYLOAD DISPLAYS — one per notification type
// ─────────────────────────────────────────────────────────────────
function AccommodationDisplay({ payload }: { payload: AccommodationPayload }) {
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-start gap-2">
        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
        <span className="text-gray-700">{payload.address}</span>
      </div>
      <div className="flex items-center gap-2">
        <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <a href={`tel:${payload.keyholderPhone}`} className="text-blue-600 hover:underline font-medium">
          {payload.keyholderName} — {payload.keyholderPhone}
        </a>
        <span className="text-gray-400">(keyholder)</span>
      </div>
      <div className="flex items-center gap-2">
        <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-gray-700">
          Check-in <strong>{payload.checkIn?.slice(0,10)}</strong> &rarr; Check-out <strong>{payload.checkOut?.slice(0,10)}</strong>
        </span>
      </div>
      {payload.coOccupants?.length > 0 && (
        <div className="flex items-start gap-2">
          <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          <span className="text-gray-600">Co-occupants: {payload.coOccupants.join(', ')}</span>
        </div>
      )}
      {payload.amenities?.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {payload.amenities.map(a => (
            <span key={a} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">{a}</span>
          ))}
        </div>
      )}
      {payload.bookingRef && (
        <p className="text-gray-400 font-mono">Booking ref: {payload.bookingRef}</p>
      )}
    </div>
  )
}

function PermitDisplay({ payload }: { payload: PermitToWorkPayload }) {
  return (
    <div className="space-y-2 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-green-50 rounded-lg px-3 py-2">
          <p className="text-gray-500">Valid from</p>
          <p className="font-semibold text-gray-900">{fmtAEST(payload.validFrom)}</p>
        </div>
        <div className="bg-red-50 rounded-lg px-3 py-2">
          <p className="text-gray-500">Valid to</p>
          <p className="font-semibold text-gray-900">{fmtAEST(payload.validTo)}</p>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
        <span className="text-gray-700">{payload.siteAddress}</span>
      </div>
      {payload.conditions?.length > 0 && (
        <div className="space-y-1">
          <p className="font-semibold text-gray-600">Conditions:</p>
          {payload.conditions.map((c, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className="text-blue-500 mt-0.5">•</span>
              <span className="text-gray-700">{c}</span>
            </div>
          ))}
        </div>
      )}
      {payload.emergencyContacts?.length > 0 && (
        <div className="space-y-1">
          <p className="font-semibold text-gray-600">Emergency contacts:</p>
          {payload.emergencyContacts.map((ec, i) => (
            <div key={i} className="flex items-center gap-2">
              <Phone className="w-3 h-3 text-gray-400" />
              <a href={`tel:${ec.phone}`} className="text-blue-600 hover:underline">
                {ec.name} ({ec.role}) — {ec.phone}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TimesheetDisplay({ payload }: { payload: TimesheetApprovedPayload }) {
  return (
    <div className="space-y-2 text-xs">
      <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-2 gap-2">
        <div>
          <p className="text-gray-500">Pay period</p>
          <p className="font-semibold text-gray-900">{payload.payPeriodLabel}</p>
        </div>
        <div className="text-right">
          <p className="text-gray-500">Est. gross</p>
          <p className="font-semibold text-green-700">${payload.grossEstimate?.toLocaleString() ?? '—'}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label:'Ordinary', val:`${payload.ordinaryHours}h`, bg:'bg-blue-50', text:'text-blue-700' },
          { label:'Overtime', val:`${payload.overtimeHours}h`, bg: payload.overtimeHours > 0 ? 'bg-amber-50' : 'bg-gray-50', text: payload.overtimeHours > 0 ? 'text-amber-700' : 'text-gray-400' },
          { label:'Night loading', val:`${payload.nightLoadingHours}h`, bg: payload.nightLoadingHours > 0 ? 'bg-purple-50' : 'bg-gray-50', text: payload.nightLoadingHours > 0 ? 'text-purple-700' : 'text-gray-400' },
        ].map(c => (
          <div key={c.label} className={`rounded-lg p-2 text-center ${c.bg}`}>
            <p className={`font-bold ${c.text}`}>{c.val}</p>
            <p className="text-gray-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>
      {payload.adjustmentsMade && payload.adjustmentReason && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-700"><strong>PM adjustment:</strong> {payload.adjustmentReason}</p>
        </div>
      )}
    </div>
  )
}

function MaterialDisplay({ payload }: { payload: MaterialReadyPayload }) {
  const meta = (MATERIAL_STATUS_META as Record<string, any>)[payload.stockStatus] ?? MATERIAL_STATUS_META['AVAILABLE_ON_SITE']
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900">{payload.materialDescription}</p>
          <p className="text-gray-500">{payload.quantity} {payload.unit}</p>
        </div>
        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${meta.bg} ${meta.text}`}>
          {meta.label}
        </span>
      </div>
      {payload.linkedTaskTitle && (
        <div className="bg-blue-50 rounded-lg px-3 py-2 flex items-start gap-1.5">
          <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-blue-700">Required for: <strong>{payload.linkedTaskTitle}</strong></p>
        </div>
      )}
    </div>
  )
}

function ITPDisplay({ payload }: { payload: ITPReleasePayload }) {
  return (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-start gap-2">
        <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-gray-900">{payload.holdPointRef} — {payload.holdPointDescription}</p>
          <p className="text-gray-600 mt-0.5">Activity released: {payload.activityReleased}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-gray-500">
        <Shield className="w-3 h-3" />
        <span>Released by {payload.releasedBy} at {fmtAEST(payload.releasedAt)}</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// NOTIFICATION CARD
// ─────────────────────────────────────────────────────────────────
interface NotificationCardProps {
  notification:  CrewNotification
  currentUser:   any
  viewMode:      'pm' | 'crew'
  onAcknowledge: (id: string) => void
  onResend:      (id: string) => void
  onEscalate:    (id: string) => void
}

function NotificationCard({
  notification: n, currentUser, viewMode, onAcknowledge, onResend, onEscalate,
}: NotificationCardProps) {
  const [expanded,   setExpanded]  = useState(false)
  const [delivery,   setDelivery]  = useState<DeliveryStatusSummary | null>(null)
  const [resending,  setResending] = useState(false)
  const [escalating, setEscalating]= useState(false)

  const meta     = NOTIFICATION_TYPE_META[n.type]
  const isUnread = n.status !== 'ACKNOWLEDGED'
  const myId     = currentUser?.id ?? 'u_pm'
  const myAck    = n.acknowledgements.find(a => a.employeeId === myId)

  async function loadDelivery() {
    if (!expanded) {
      const d = await getDeliveryStatusSummary(n.id)
      setDelivery(d)
    }
    setExpanded(v => !v)
  }

  async function handleResend() {
    setResending(true)
    const count = await resendNotification(n.id)
    setResending(false)
    onResend(n.id)
    if (count > 0) alert(`Reminder sent to ${count} crew member${count !== 1 ? 's' : ''}.`)
    else alert('All crew have already acknowledged this notification.')
  }

  async function handleEscalate() {
    setEscalating(true)
    await escalateToWHSOAlert(n.id, myId, currentUser?.fullName ?? 'PM')
    setEscalating(false)
    onEscalate(n.id)
    alert('Escalated to WHS Alerts as a DRAFT WHSO alert. Review and send from the WHS tab.')
  }

  // Pick the rich payload display component
  function PayloadDisplay() {
    if (!expanded) return null
    try {
      const p = n.payload as any
      if (n.type === 'ACCOMMODATION')      return <AccommodationDisplay payload={p} />
      if (n.type === 'PERMIT_TO_WORK')     return <PermitDisplay payload={p} />
      if (n.type === 'TIMESHEET_APPROVED') return <TimesheetDisplay payload={p} />
      if (n.type === 'MATERIAL_READY')     return <MaterialDisplay payload={p} />
      if (n.type === 'ITP_RELEASE')        return <ITPDisplay payload={p} />
    } catch { /* payload shape mismatch — render nothing */ }
    return null
  }

  const pendingCount = n.employeeIds.length - n.acknowledgements.length
  const canResend    = viewMode === 'pm' && n.requiresAck && pendingCount > 0

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all
      ${PRIORITY_RING[n.priority] ?? ''}
      ${isUnread ? '' : 'opacity-70'}
      ${n.status === 'ACKNOWLEDGED' ? 'border-gray-100' : 'border-gray-200'}`}>

      {/* Main row — always visible */}
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Type icon */}
        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-base
          ${meta?.category === 'safety' ? 'bg-red-100' :
            meta?.category === 'payroll' ? 'bg-green-100' :
            meta?.category === 'quality' ? 'bg-blue-100' : 'bg-gray-100'}`}>
          {meta?.icon ?? '🔔'}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${PRIORITY_BADGE[n.priority]}`}>
                  {n.priority}
                </span>
                <span className="text-xs text-gray-400">{meta?.label ?? n.type.replace(/_/g,' ')}</span>
                {n.status === 'ACKNOWLEDGED' && (
                  <span className="text-xs text-green-600 flex items-center gap-0.5">
                    <CheckCircle className="w-3 h-3" /> All acked
                  </span>
                )}
              </div>
              <p className={`text-sm font-semibold leading-snug ${isUnread ? 'text-gray-900' : 'text-gray-600'}`}>
                {n.heading}
              </p>
              {!expanded && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
              )}
            </div>
            <div className="flex-shrink-0 flex items-center gap-1.5">
              <span className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(n.createdAt)}</span>
              <button onClick={loadDelivery}
                className="p-1 rounded hover:bg-gray-100">
                {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
            </div>
          </div>

          {/* Delivery bar (PM view) */}
          {viewMode === 'pm' && n.requiresAck && n.employeeIds.length > 0 && (
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[80px]">
                <div
                  className="bg-green-500 h-1.5 rounded-full"
                  style={{ width:`${n.employeeIds.length > 0 ? (n.acknowledgements.length / n.employeeIds.length) * 100 : 0}%` }}
                />
              </div>
              <span className="text-xs text-gray-500">
                {n.acknowledgements.length}/{n.employeeIds.length} acked
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
          {/* Full body */}
          <p className="text-xs text-gray-700 leading-relaxed">{n.body}</p>

          {/* Required action */}
          {n.requiredAction && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>Required action: </strong>{n.requiredAction}
              </p>
            </div>
          )}

          {/* Type-specific rich payload */}
          <PayloadDisplay />

          {/* Delivery detail (PM view) */}
          {viewMode === 'pm' && delivery && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Delivery status</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label:'Total', val:delivery.total, colour:'text-gray-700' },
                  { label:'Acknowledged', val:delivery.acknowledged, colour:'text-green-700' },
                  { label:'Pending', val:delivery.pending, colour:delivery.pending>0?'text-amber-700':'text-gray-400' },
                ].map(s => (
                  <div key={s.label} className="text-center bg-gray-50 rounded-lg py-2">
                    <p className={`text-lg font-bold ${s.colour}`}>{s.val}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
              {delivery.acknowledgedBy.length > 0 && (
                <div className="space-y-1">
                  {delivery.acknowledgedBy.map(a => (
                    <div key={a.employeeId} className="flex items-center gap-2 text-xs">
                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                      <span className="font-medium text-gray-700">{a.employeeName}</span>
                      <span className="text-gray-400 ml-auto">{fmtAEST(a.acknowledgedAt)}</span>
                    </div>
                  ))}
                  {delivery.pendingIds.length > 0 && (
                    <div className="pt-0.5 space-y-1">
                      {delivery.pendingIds.map(id => {
                        const emp = (MOCK_EMPLOYEES as any[]).find(e => e.id === id)
                        return (
                          <div key={id} className="flex items-center gap-2 text-xs">
                            <Circle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                            <span className="text-gray-500">{emp?.fullName ?? id}</span>
                            <span className="text-amber-600 ml-auto font-medium">Pending</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Source */}
          <p className="text-xs text-gray-400">
            Source: {n.sourceModule}
            {n.sourceRecordId && ` · ${n.sourceRecordId}`}
          </p>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap pt-0.5">
            {/* Self-acknowledge (crew view or PM acknowledging own) */}
            {n.requiresAck && !myAck && (
              <button
                onClick={() => onAcknowledge(n.id)}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Acknowledge
              </button>
            )}
            {myAck && (
              <span className="text-xs text-green-700 flex items-center gap-1 px-3 py-1.5">
                <CheckCircle className="w-3.5 h-3.5" /> You acknowledged {fmtAEST(myAck.acknowledgedAt)}
              </span>
            )}

            {/* PM-only actions */}
            {viewMode === 'pm' && (
              <>
                {canResend && (
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50">
                    {resending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {resending ? 'Sending...' : `Resend (${pendingCount} pending)`}
                  </button>
                )}
                <button
                  onClick={handleEscalate}
                  disabled={escalating}
                  className="px-3 py-1.5 rounded-lg border border-amber-200 text-xs text-amber-700 hover:bg-amber-50 flex items-center gap-1.5 disabled:opacity-50">
                  {escalating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                  {escalating ? 'Escalating...' : 'Escalate to WHS Alert'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// NOTIFICATIONS HUB TAB — Phase 5 Full Implementation
// ─────────────────────────────────────────────────────────────────
export function NotificationsHubTab({ currentUser }: { currentUser: any }) {
  const [groups,       setGroups]       = useState<GroupedNotifications[]>([])
  const [allNotifs,    setAllNotifs]    = useState<CrewNotification[]>([])
  const [loading,      setLoading]      = useState(true)
  const [viewMode,     setViewMode]     = useState<'pm' | 'crew'>('pm')
  const [searchQuery,  setSearchQuery]  = useState('')
  const [typeFilter,   setTypeFilter]   = useState<NotificationType | 'ALL'>('ALL')
  const [jobFilter,    setJobFilter]    = useState<string>('ALL')
  const [dateFilter,   setDateFilter]   = useState<'ALL' | 'TODAY' | 'WEEK'>('ALL')
  const [exporting,    setExporting]    = useState(false)
  const [refreshKey,   setRefreshKey]   = useState(0)

  const myId = currentUser?.id ?? 'u_pm'

  const load = useCallback(async () => {
    setLoading(true)
    const baseFilter = viewMode === 'crew' ? { employeeIds: [myId] } : undefined
    let notifs: CrewNotification[]

    if (searchQuery.trim()) {
      notifs = await searchNotifications(searchQuery, baseFilter)
    } else {
      notifs = await (await import('../services/notificationBusService')).getCrewNotifications(baseFilter)
    }

    setAllNotifs(notifs)

    // Apply client-side filters
    let filtered = notifs
    if (typeFilter !== 'ALL') filtered = filtered.filter(n => n.type === typeFilter)
    if (jobFilter !== 'ALL')  filtered = filtered.filter(n => n.jobIds.includes(jobFilter))
    if (dateFilter === 'TODAY') {
      const today = new Date().toDateString()
      filtered = filtered.filter(n => new Date(n.createdAt).toDateString() === today)
    } else if (dateFilter === 'WEEK') {
      const weekAgo = new Date(Date.now() - 7 * 86_400_000)
      filtered = filtered.filter(n => new Date(n.createdAt) >= weekAgo)
    }

    // Re-group after filtering
    const now       = new Date()
    const today     = now.toDateString()
    const yesterday = new Date(now.getTime() - 86_400_000).toDateString()
    const weekAgo   = new Date(now.getTime() - 7 * 86_400_000)

    const buckets: Record<string, CrewNotification[]> = { Today:[], Yesterday:[], 'This week':[], Earlier:[] }
    for (const n of filtered) {
      const d = new Date(n.createdAt).toDateString()
      if (d === today)           buckets['Today'].push(n)
      else if (d === yesterday)  buckets['Yesterday'].push(n)
      else if (new Date(n.createdAt) >= weekAgo) buckets['This week'].push(n)
      else                       buckets['Earlier'].push(n)
    }
    setGroups(
      (['Today','Yesterday','This week','Earlier'] as const)
        .filter(g => buckets[g].length > 0)
        .map(g => ({ group: g as any, date: g, items: buckets[g] }))
    )
    setLoading(false)
  }, [viewMode, searchQuery, typeFilter, jobFilter, dateFilter, myId, refreshKey])

  useEffect(() => { load() }, [load])

  // Derived values for filters
  const allTypes = useMemo(() => {
    const types = new Set(allNotifs.map(n => n.type))
    return [...types] as NotificationType[]
  }, [allNotifs])

  const allJobIds = useMemo(() => {
    const ids = new Set(allNotifs.flatMap(n => n.jobIds))
    return [...ids]
  }, [allNotifs])

  const totalCount  = groups.reduce((sum, g) => sum + g.items.length, 0)
  const unreadCount = allNotifs.filter(n => n.status !== 'ACKNOWLEDGED').length

  async function handleAcknowledge(notificationId: string) {
    await acknowledgeNotification(notificationId, myId, currentUser?.fullName ?? 'PM')
    setRefreshKey(k => k + 1)
  }

  function handleResendRefresh() {
    setRefreshKey(k => k + 1)
  }

  function handleEscalateRefresh() {
    setRefreshKey(k => k + 1)
  }

  async function handleExportCSV() {
    setExporting(true)
    const csv = await exportNotificationsToCSV(
      typeFilter !== 'ALL' ? { types: [typeFilter] } : undefined
    )
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `crew-notifications-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setExporting(false)
  }

  // Type metadata for filter pills
  const typeMetaForFilter = (type: NotificationType) => NOTIFICATION_TYPE_META[type]

  return (
    <div className="space-y-4">
      {/* ── Header bar ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* PM / Crew view toggle */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          <button onClick={() => setViewMode('pm')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${viewMode==='pm'?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            <Users className="w-3.5 h-3.5" /> PM View
          </button>
          <button onClick={() => setViewMode('crew')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${viewMode==='crew'?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            <Eye className="w-3.5 h-3.5" /> Crew View (sim)
          </button>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-gray-500 ml-auto">
          <span>{totalCount} notification{totalCount!==1?'s':''}</span>
          {unreadCount > 0 && (
            <span className="bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>

        {/* Export CSV */}
        <button
          onClick={handleExportCSV}
          disabled={exporting || totalCount === 0}
          className="px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-40">
          {exporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export CSV
        </button>
      </div>

      {/* ── View mode description ──────────────────────────────── */}
      {viewMode === 'crew' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-xs text-blue-700">
          <Eye className="w-3.5 h-3.5 flex-shrink-0" />
          <span><strong>Simulating crew view</strong> for {currentUser?.fullName ?? 'you'} — showing only notifications addressed to this account. Crew see this exact view in the mobile app.</span>
        </div>
      )}

      {/* ── Search ─────────────────────────────────────────────── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search notifications by heading, body, or type..."
          className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────────── */}
      <div className="space-y-2">
        {/* Type pills */}
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setTypeFilter('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${typeFilter==='ALL'?'bg-gray-800 text-white border-gray-800':'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            All ({allNotifs.length})
          </button>
          {allTypes.map(t => {
            const m = typeMetaForFilter(t)
            const count = allNotifs.filter(n => n.type === t).length
            return (
              <button key={t}
                onClick={() => setTypeFilter(typeFilter === t ? 'ALL' : t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors flex items-center gap-1 ${typeFilter===t?'bg-gray-800 text-white border-gray-800':'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <span>{m?.icon}</span>
                <span>{m?.label ?? t.replace(/_/g,' ')} ({count})</span>
              </button>
            )
          })}
        </div>

        {/* Job + date secondary filters */}
        <div className="flex gap-2 flex-wrap">
          <select
            value={jobFilter}
            onChange={e => setJobFilter(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 bg-white">
            <option value="ALL">All jobs</option>
            {allJobIds.map(id => {
              const job = (MOCK_JOBS as any[]).find(j => j.id === id)
              return <option key={id} value={id}>{job?.jobNumber ?? id}</option>
            })}
          </select>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {(['ALL','TODAY','WEEK'] as const).map(d => (
              <button key={d}
                onClick={() => setDateFilter(d)}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${dateFilter===d?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
                {d === 'ALL' ? 'All time' : d === 'TODAY' ? 'Today' : 'This week'}
              </button>
            ))}
          </div>
          <button onClick={load} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 ml-auto">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Notification feed ──────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading notifications...
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Bell className="w-10 h-10 text-gray-200 mx-auto" />
          <p className="text-sm text-gray-400 font-medium">
            {searchQuery ? `No notifications matching "${searchQuery}"` : 'No notifications for this filter.'}
          </p>
          {(typeFilter !== 'ALL' || jobFilter !== 'ALL' || dateFilter !== 'ALL' || searchQuery) && (
            <button
              onClick={() => { setTypeFilter('ALL'); setJobFilter('ALL'); setDateFilter('ALL'); setSearchQuery('') }}
              className="text-xs text-blue-600 hover:underline">
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(g => (
            <div key={g.group}>
              {/* Date group header */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{g.group}</span>
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">{g.items.length} notification{g.items.length!==1?'s':''}</span>
              </div>

              {/* Notification cards */}
              <div className="space-y-2">
                {g.items.map(n => (
                  <NotificationCard
                    key={n.id}
                    notification={n}
                    currentUser={currentUser}
                    viewMode={viewMode}
                    onAcknowledge={handleAcknowledge}
                    onResend={handleResendRefresh}
                    onEscalate={handleEscalateRefresh}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Module connection audit badge ─────────────────────── */}
      <div className="border border-green-200 bg-green-50 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-green-800">Module Connection Audit — Phase 5 Complete</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-xs text-green-700">
          {[
            'Crew Roster','WHS Credentials','Tasks & Photos',
            'WHS Inbox','WHSO Alerts','Travel / Accommodation',
            'Incidents (auto)','SWMS (revision)','Permits to Work',
            'Toolbox Talks','Plant & Equipment','Purchase Orders / GRN',
            'ITP / Hold Points','Work Instructions','Timesheets (approval)',
            'Environmental','Progress Claims (opt.)',
          ].map(m => (
            <div key={m} className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
              <span className="truncate">{m}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
